import React, { useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import api from "../../api";

// Server paging avoids silently truncating projects and active-user choices.
export default function PagedOptions({ path, label, revision, selectedId, onSelect }) {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setResult(null);
    setError(false);
    const params = new URLSearchParams({ q: query, offset: page * 10, limit: 10 });
    api({ method: "get", url: `/surveys/${path}${path.includes("?") ? "&" : "?"}${params}` })
      .then(({ data }) => {
        if (!active) return;
        if (page && page * 10 >= data.total) {
          setPage(Math.max(0, Math.ceil(data.total / 10) - 1));
          return;
        }
        setResult(data);
      })
      .catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [path, query, page, revision, retry]);
  return (
    <Box component="section" aria-label={label}>
      <Box component="form" onSubmit={(event) => {
        event.preventDefault(); setPage(0); setQuery(search.trim()); setRetry((n) => n + 1);
      }}>
        <TextField label={`Search ${label.toLowerCase()}`} size="small" fullWidth
          value={search} onChange={(event) => setSearch(event.target.value)}
          inputProps={{ maxLength: 200 }} sx={{ background: "white", borderRadius: 1 }} />
        <Button type="submit">Search</Button>
        <Button onClick={() => { setSearch(""); setQuery(""); setPage(0); setRetry((n) => n + 1); }}>Clear</Button>
      </Box>
      {error ? <Alert severity="error">Unable to load {label.toLowerCase()}.
        <Button onClick={() => setRetry((n) => n + 1)}>Retry</Button>
      </Alert> : !result ? <CircularProgress size={20} aria-label={`Loading ${label.toLowerCase()}`} /> : <>
        {!result.items.length && <Typography>No matching {label.toLowerCase()}.</Typography>}
        {result.items.map((item) => <Button key={item.id} fullWidth
          aria-pressed={selectedId === item.id} onClick={() => onSelect(item)}
          sx={{ textAlign: "left", justifyContent: "flex-start", overflowWrap: "anywhere", border: selectedId === item.id ? "1px solid currentColor" : "1px solid transparent" }}>
          {item.name}{item.email ? ` · ${item.email}` : item.client_name ? ` · ${item.client_name}` : ""}
        </Button>)}
        <Typography variant="caption" aria-live="polite">
          {result.total ? `${page * 10 + 1}–${Math.min((page + 1) * 10, result.total)}` : "0"} of {result.total}
        </Typography>
        <Stack direction="row">
          <Button aria-label={`Previous ${label.toLowerCase()} page`} disabled={!page} onClick={() => setPage(page - 1)}>Previous</Button>
          <Button aria-label={`Next ${label.toLowerCase()} page`} disabled={(page + 1) * 10 >= result.total} onClick={() => setPage(page + 1)}>Next</Button>
        </Stack>
      </>}
    </Box>
  );
}
